import useLatest from 'hooks/useLatestRef';
import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { createSocket } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';

const useReconnectWs = (config: { enabled: boolean }) => {
  const current = useAuthStore((s) => s.current);
  const wsConnected = useAuthStore((s) => s.wsConnected);

  const latestWsConnected = useLatest(wsConnected);
  const latestToken = useLatest(current?.token);

  const debouncedReconnect = useMemo(
    () =>
      debounce(
        () => {
          if (latestWsConnected.current) {
            return;
          }

          if (!latestToken.current) {
            return;
          }

          createSocket(latestToken.current);
        },
        500,
        { maxWait: 2000 }
      ),
    []
  );

  // ! deprecate this unless we still need it
  /**
   * focus - tab or window gains focus
   * online - network connection is re-established
   * pageshow - page is loaded from bfcache (back/forward cache)
   * visibilitychange - tab becomes visible again
   * resume - chrome resumes mobile tab from suspended state
   */
  useEffect(() => {
    const reconnect = () => {
      if (wsConnected) {
        return;
      }

      debouncedReconnect();
    };

    const onFocus = () => reconnect();
    const onOnline = () => reconnect();
    const onResume = () => reconnect();
    const onPageShow = () => reconnect();
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      return debouncedReconnect();
    };

    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('resume', onResume);
      document.removeEventListener('visibilitychange', onVisible);
      debouncedReconnect.cancel();
    };

    if (!config.enabled) {
      return cleanup;
    }

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('resume', onResume);
    document.addEventListener('visibilitychange', onVisible);

    return cleanup;
  }, [debouncedReconnect, config.enabled, wsConnected]);
};

export default useReconnectWs;
